
import React from "react";
import MainLayout from "@/components/layout/MainLayout";
import { useLanguage } from "@/context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const Privacy = () => {
  const { language } = useLanguage();
  const isChineseMode = language === 'zh-TW';

  return (
    <MainLayout>
      {/* 主要容器 - 漸層背景從橘色淡色到白色 */}
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
        {/* 頁面標題區塊 */}
        <section className="pt-24 pb-12 px-4 md:px-8 lg:px-12">
          <div className="container max-w-4xl mx-auto text-center">
            {/* 主標題 */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {isChineseMode ? "隱私權政策" : "Privacy Policy"}
            </h1>
            {/* 橘色分隔線 */}
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            {/* 副標題說明 */}
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {isChineseMode 
                ? "我們重視您的隱私權，以下說明我們如何收集、使用與保護您的個人資料"
                : "We value your privacy. This policy explains how we collect, use, and protect your personal information"
              }
            </p>
          </div>
        </section>

        {/* 內容區塊 */}
        <div className="container max-w-4xl mx-auto px-4 md:px-8 lg:px-12 pb-16 space-y-8">
          
          {/* 第一節：資料收集類型 */}
          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "一、資料收集類型" : "1. Types of Data We Collect"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode
                    ? "我們可能收集您在註冊或使用本平台時提供的資訊，包括但不限於：姓名、暱稱、年齡、性別、國籍、電子信箱、地點資訊、租屋經驗、評論、上傳圖片與登入紀錄。"
                    : "We may collect information you provide during registration or use of the Platform, including but not limited to: name, nickname, age, gender, nationality, email address, location data, rental reviews, uploaded images, and login activity."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 第二節：資料使用目的 */}
          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "二、資料使用目的" : "2. Purposes of Data Use"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* 使用目的列表項目 */}
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "提供與改進平台服務，包括會員系統、租屋資料展示與互動功能。" : "To provide and improve our services, including membership functions, listing displays, and interactive features."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "用於平台數據分析與使用者行為統計。" : "For analytics and user behavior statistics."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "為了用戶體驗，與第三方服務（如地圖、翻譯、寄信 API）整合。" : "To enhance user experience via third-party integrations (e.g., maps, translation, email)."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "未來如啟用付費功能，將與合法金流平台共享支付資訊。" : "For future payment features, to share data with authorized payment processors."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 3 */}
          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "三、資料保存與保護" : "3. Data Storage & Security"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode
                    ? "您的資料儲存於 Supabase 或其他合約雲端服務，並採用合理的資訊安全措施防止資料外洩。"
                    : "Your data is stored on Supabase or other contracted cloud providers, with reasonable security measures in place to protect it."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "四、資料分享與第三方服務" : "4. Data Sharing & Third-Party Services"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode
                    ? "我們不會將您的個人資料販售或任意提供給第三方，但為了執行平台功能，可能與技術合作方（如 Google Maps、翻譯服務、電子郵件寄送平台）分享必要資料。"
                    : "We do not sell or disclose your personal data to third parties, except when required to operate features via technical partners (e.g., Google Maps, translation APIs, email delivery services)."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "五、使用者權利" : "5. Your Rights"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "您有權查詢、修改、刪除您於平台所提供之個人資料。" : "You have the right to access, modify, and delete your personal data stored on the Platform."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "您可要求我們停止蒐集、處理或使用您的資料。" : "You may request that we stop collecting, processing, or using your data."}
                  </p>
                </div>
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-justify">
                    {isChineseMode ? "請透過 customer.service@trustay.app 聯絡我們以行使上述權利。" : "Contact us at customer.service@trustay.app to exercise these rights."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "六、資料保留期限" : "6. Data Retention Period"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode
                    ? "資料將保存至您刪除帳號或依法不得再保存為止。公開發表之租屋內容可能保留於平台供查閱用途。"
                    : "Data will be retained until you delete your account or until retention is no longer required by law. Published rental content may be kept for public reference."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "七、隱私政策更新" : "7. Changes to This Policy"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-justify">
                  {isChineseMode
                    ? "本政策可能因應法規或業務需要調整。"
                    : "We may update this policy in response to regulatory or operational changes."
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 第八節：聯絡方式 - 調整排版避免超出框位 */}
          <Card className="border-l-4 border-orange-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                {isChineseMode ? "八、聯絡方式" : "8. Contact Us"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 rounded-lg p-6">
                {/* 聯絡說明文字 */}
                <p className="text-gray-700 leading-relaxed mb-6 text-center">
                  {isChineseMode
                    ? "如您對本政策有任何疑問，請聯絡："
                    : "If you have any questions about this policy, please contact:"
                  }
                </p>
                {/* 電子信箱區塊 - 改善排版，使用較小字體和自動換行 */}
                <div className="bg-white rounded-lg border border-orange-200 p-4">
                  <div className="text-center">
                    <span className="font-semibold text-orange-700 text-sm md:text-base break-all">
                      customer.service@trustay.app
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default Privacy;
